//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useEffect, useContext } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles, Container, Grid, CircularProgress } from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/core/styles';
import ArrowRightAltIcon from '@material-ui/icons/ArrowForward';
import LinearProgress from '@material-ui/core/LinearProgress';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Input, { isValidPhoneNumber, isPossiblePhoneNumber } from 'react-phone-number-input/input';
import { useHistory } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { LayoutContext, AppContext } from '../../common/contexts';
import useCountryStateCity from '../../common/hooks/useCountryStateCity';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { validateEmail } from '../../common/emailHelper';
import { BASE_URL } from '../../common/constants';
import { PATH_ADVERTISE } from '../../system/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { saveAdCusData } from '../../common/services/adServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdCountryAutocomplete from '../../common/elemental/AfitdCountryAutocomplete';
import AfitdTextField from '../../common/elemental/AfitdTextField';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import _get from 'lodash/get';

import _get from 'lodash/get';
import _set from 'lodash/set';
import _includes from 'lodash/includes';
import _find from 'lodash/find';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: { padding: 20 },
    exampleWithNoPropsOnlyStyles: { color: 'red', marginTop: 30, marginBottom: 40 },
    nonReactElementExample: { color: 'blue', marginTop: 50, cursor: 'pointer' },
    container: {
        width: '100%',
        // height: '640px',
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '48px 112px 48px 112px',
        [theme.breakpoints.down('sm')]: { padding: '44px 0px' },
    },
    heading: {
        fontSize: '26px',
        fontWeight: 'bold',
        textAlign: 'center',
    },
    fieldLabel: {
        fontWeight: 'bold',
        fontSize: '14px',
        lineHeight: '16px',
        marginTop: '20px',
    },
    progressGrid: {
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: { justifyContent: 'center', margin: '0 auto', width: '100%' },
    },
    gridBody: {
        marginTop: '38px',
    },
    gridBodyFooter: { marginTop: '38px', [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' } },
    nextButtonContainer: {
        textAlign: 'right',
        marginTop: '32px',
        marginRight: 5,
        [theme.breakpoints.down('sm')]: { marginRight: 0 },
    },
    nextButton: {
        textAlign: 'right',
        marginTop: '32px',
        marginRight: 5,
        [theme.breakpoints.down('sm')]: { marginRight: 0 },
    },
    footerGridItem: {
        [theme.breakpoints.down('sm')]: { paddingRight: 8 },
    },
    phoneInput: {
        fontSize: '16px',
        '&:focus': {
            borderColor: `${theme.palette.secondary.main} !important`,
        },
        borderWidth: '2px',
        outline: 'none !important',
        padding: '17px',
        borderRadius: '4px',
        border: '2px solid #c4c4c4',
        display: 'block',
        width: '100%',
        marginTop: '9px',
        // maxWidth: '438px',
        '&::placeholder': {
            color: '#a2a2a2',
        },
    },
    phoneInputError: {
        fontSize: '16px',
        '&:focus': {
            borderColor: `#f44336 !important`,
        },
        borderWidth: '2px',
        outline: 'none !important',
        padding: '17px',
        borderRadius: '4px',
        border: '2px solid #f44336',
        display: 'block',
        width: '100%',
        marginTop: '9px',
        // maxWidth: '438px',
        '&::placeholder': {
            color: '#a2a2a2',
        },
    },
    phoneInputErrorMessage: {
        color: '#f44336',
        fontSize: '0.75rem !important',
        marginTop: '3px',
        lineHeight: '1.66',
        marginLeft: '13px',
    },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        isDirty,
        // setIsDirty,
        // formData,
        handleChangeData,
        handleChangeCountry,
        handleChangeState,
        handleChangeCity,
        country,
        state,
        city,
        countryLov,
        stateLov,
        cityLov,
        handleNextButton,
        errors,
        wrongEmailErrorText,
        customerData,
        isLoading,
    } = params;
    const { companyNameError, countryError, stateError, cityError, emailAddressError, phoneError, contactNameError } = errors;
    // const isDirty =
    //     formData.companyName === '' && state === '' && country === '' && city === '' && formData.email === '' && formData.phone === '' && formData.contactName === '';

    const defns = {
        helloText: { variant: 'h4' },
        textFieldExample: { value: 'Hello TextField' },
        nonReactElementExample: {
            onClick: evt => {
                alert('Clicked!');
            },
        },
        gridBody: {
            container: true,
            spacing: 2,
        },
        gridBodyFooter: {
            container: true,
            spacing: 2,
        },
        itemGrid: { item: true, xl: 6, lg: 6, md: 6, sm: 12, xs: 12 },
        progressGrid: { item: true, xs: 2 },
        companyName: {
            name: 'companyName',
            placeholder: '+ Ex. Dream Team',
            onChange: handleChangeData,
            value: customerData.companyName,
            error: companyNameError,
            helperText: companyNameError ? 'Company Name is required' : undefined,
            disabled: isLoading,
        },
        contactName: {
            name: 'poc',
            placeholder: '+ Ex. Jack Sparrow',
            onChange: handleChangeData,
            value: customerData.poc,
            error: contactNameError,
            helperText: contactNameError ? 'Point of Contact Name is required' : undefined,
            disabled: isLoading,
        },
        phone: {
            name: 'phone',
            placeholder: '+ Ex. 1(800) 555-3295',
            onChange: handleChangeData,
            value: customerData.phone,
            type: 'number',
            error: phoneError,
            helperText: phoneError ? 'Telephone Number is required' : undefined,
            disabled: isLoading,
        },
        email: {
            name: 'email',
            placeholder: '+ Ex. john@dreamteam',
            type: 'email',
            onChange: handleChangeData,
            value: customerData.email,
            error: emailAddressError,
            helperText: emailAddressError ? (wrongEmailErrorText && customerData.email ? wrongEmailErrorText : 'Email Address is required') : undefined,
            disabled: isLoading,
        },
        country: {
            onChange: handleChangeCountry,
            value: country,
            options: countryLov,
            id: 'country',
            placeholder: '+ Ex. United State',
            error: countryError,
            helperText: countryError ? 'Country is required' : undefined,
            disabled: isLoading,
        },
        state: {
            disabled: !country || isLoading,
            onChange: handleChangeState,
            value: state,
            id: 'state',
            options: stateLov,
            placeholder: '+ Ex. Alabama',
            error: stateError,
            helperText: stateError ? 'State/Province is required' : undefined,
        },
        city: {
            disabled: !state || isLoading,
            onChange: handleChangeCity,
            value: city,
            id: 'city',
            options: cityLov,
            placeholder: '+ Ex. City',
            error: cityError,
            helperText: cityError ? 'City is required' : undefined,
        },
        footerGridItem: {
            xl: 5,
            lg: 5,
            md: 5,
            sm: 12,
            xs: 12,
        },
        progress: {
            variant: 'determinate',
            value: 20,
        },
        companyNameErrorText: { hidden: !companyNameError },
        countryErrorText: { hidden: !countryError },
        stateErrorText: { hidden: !stateError },
        cityErrorText: { hidden: !cityError },
        nextButton: { disabled: !isDirty || isLoading, onClick: handleNextButton, color: 'primary' },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const BorderLinearProgress = withStyles(theme =>
    createStyles({
        root: {
            height: 10,
            borderRadius: 5,
        },
        colorPrimary: {
            backgroundColor: '#F0F2F6',
        },
        bar: {
            borderRadius: 5,
            backgroundColor: '#1D8A8B',
        },
    }),
)(LinearProgress);

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const Step1Page = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const history = useHistory();
    const theme = useTheme();
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    const appContext = useContext(AppContext);
    const { currentEmployer, lovs, userId, employerId } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { scrollToPageTop, customerData, setCustomerData } = layoutContext;
    // const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    // const { phone } = customerData;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { propExample } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isInitPage, setIsInitPage] = React.useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [wrongEmailErrorText, setWrongEmailErrorText] = useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    // const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState(false);

    const [errors, setErrors] = React.useState({
        companyNameError: false,
        emailAddressError: false,
        contactNameError: false,
        phoneError: false,
        stateError: false,
        countryError: false,
        cityError: false,
    });
    const [formData, setFormData] = React.useState({
        companyName: '',
        email: '',
        contactName: '',
        phone: '',
    });
    const { countryLov, stateLov, cityLov, country, setCountry, state, setState, city, setCity, setCityLocation } = useCountryStateCity({
        defaultCountryLov: _get(lovs, 'countryLov'),
    });

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // on mount, scroll to the top of the page
    useEffect(() => {
        scrollToPageTop();
    }, [scrollToPageTop]);

    useEffect(() => {
        if (!isInitPage) {
            const adCusId = _get(customerData, 'adCusId');

            if (!adCusId && currentEmployer) {
                const country = _get(currentEmployer, 'country', null);
                const state = _get(currentEmployer, 'state', null);
                const city = _get(currentEmployer, 'city', null);
                const cityLocation = _get(currentEmployer, 'cityLocation', null);

                setCountry(country);
                setState(state);
                setCity(city);
                setCityLocation(cityLocation);
                const members = _get(currentEmployer, 'members', []);
                const member = _find(members, o => o.userId === userId);

                setCustomerData(prev => ({ ...prev, email: member?.email, country, state, city, cityLocation }));

                setIsInitPage(true);
            } else if (adCusId) {
                const country = _get(customerData, 'country', null);
                const state = _get(customerData, 'state', null);
                const city = _get(customerData, 'city', null);
                const cityLocation = _get(customerData, 'cityLocation', null);

                setCountry(country);
                setState(state);
                setCity(city);
                setCityLocation(cityLocation);

                setIsInitPage(true);
            }
        }
    }, [isInitPage, userId, currentEmployer, customerData, setCountry, setState, setCity, setCityLocation, setCustomerData]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const defaultCountryOption = _find(_get(lovs, 'countryLov'), o => o.name === country);

    const countryCode = _get(defaultCountryOption, 'iso2');

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleChangeData = React.useCallback(
        evt => {
            const { name, value } = evt.target;
            if (name === 'email') {
                const result = validateEmail(value);
                if (result === null) {
                    setErrors({ ...errors, emailAddressError: true });
                    setWrongEmailErrorText('Please Enter a valid Email.');
                } else {
                    setErrors({ ...errors, emailAddressError: false });
                    setWrongEmailErrorText('');
                }
            }
            // setFormData({ ...formData, [name]: value });
            setCustomerData({ ...customerData, [name]: value });
            setIsDirty(true);
        },
        [customerData, errors, setCustomerData],
    );

    const handleChangeCountry = (evt, option) => {
        const countryValue = option?.name;

        setCountry(countryValue);
        setState(null);
        setCity(null);
        setCityLocation(null);
        setIsDirty(true);
        setCustomerData({ ...customerData, country: countryValue });
    };

    const handleChangeState = (evt, option) => {
        const stateValue = option?.name;

        setState(stateValue);
        setCity(null);
        setCityLocation(null);
        setIsDirty(true);
        setCustomerData({ ...customerData, state: stateValue });
    };

    const handleChangeCity = (evt, option) => {
        const cityName = _get(option, 'name') === undefined ? option : option.name;
        const cityLocation = option?.location || null;

        setCity(cityName);
        setIsDirty(true);
        setCustomerData({ ...customerData, city: cityName, cityLocation });
    };

    const onChangePhone = val => {
        if (val) {
            setCustomerData({ ...customerData, phone: val });
            if (isPossiblePhoneNumber(val) && isValidPhoneNumber(val)) {
                setPhoneError(false);
                setErrors({ ...errors, phoneError: false });
                setIsDirty(true);
            } else {
                setPhoneError(true);
                setErrors({ ...errors, phoneError: true });
            }
        } else {
            setPhoneError(true);
            setErrors({ ...errors, phoneError: true });
        }
    };

    const validate = () => {
        let newErrors = { ...errors };
        !customerData.companyName && _set(newErrors, 'companyNameError', true);
        customerData.companyName && _set(newErrors, 'companyNameError', false);
        !customerData.poc && _set(newErrors, 'contactNameError', true);
        customerData.poc && _set(newErrors, 'contactNameError', false);
        !customerData.phone && setPhoneError(true);
        customerData.phone && isPossiblePhoneNumber(customerData.phone) && isValidPhoneNumber(customerData.phone) && setPhoneError(false);
        !customerData.email && _set(newErrors, 'emailAddressError', true);
        customerData.email && _set(newErrors, 'emailAddressError', false);
        !country && _set(newErrors, 'countryError', true);
        country && _set(newErrors, 'countryError', false);
        !state && _set(newErrors, 'stateError', true);
        state && _set(newErrors, 'stateError', false);
        !city && _set(newErrors, 'cityError', true);
        city && _set(newErrors, 'cityError', false);
        setErrors(newErrors);
        return !_includes(newErrors, true);
    };

    const handleNextButton = async () => {
        if (validate()) {
            setIsLoading(true);
            // send to Firebase
            // console.log({ data: { ...customerData, employerId }, callBack: () => history.push(`${BASE_URL}${PATH_ADVERTISE}/step2`) });
            await saveAdCusData({
                data: { ...customerData, employerId, userId },
                callBack: () => {
                    setIsLoading(false);
                    history.push(`${BASE_URL}${PATH_ADVERTISE}/step2`);
                },
                setCustomerData,
            });
        }
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        isDirty,
        setIsDirty,
        formData,
        setFormData,
        handleChangeData,
        handleChangeCountry,
        handleChangeState,
        handleChangeCity,
        handleNextButton,
        country,
        state,
        city,
        countryLov,
        stateLov,
        cityLov,
        errors,
        history,
        wrongEmailErrorText,
        customerData,
        isLoading,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <Container {...defns.container}>
                <div {...defns.heading}>Information About You</div>
                <Grid {...defns.gridBody}>
                    <Grid {...defns.itemGrid}>
                        <p {...defns.fieldLabel}>Company Name: *</p>
                        {/* <AfitdTextField {...defns.companyName} /> */}
                        <AfitdTextField {...defns.companyName} />
                        {/* <span {...defns.companyNameErrorText}>Company Name is required</span> */}
                    </Grid>
                    <Grid {...defns.itemGrid}>
                        <p {...defns.fieldLabel}>Your Email Address: *</p>
                        <AfitdTextField {...defns.email} />
                    </Grid>
                    <Grid {...defns.itemGrid}>
                        <p {...defns.fieldLabel}>Headquarters/Head Office Location: *</p>
                        <AfitdCountryAutocomplete {...defns.country} />
                        {/* <span {...defns.countryErrorText}>Headquarters/Head Office Location is required</span> */}
                    </Grid>
                    <Grid {...defns.itemGrid}>
                        <p {...defns.fieldLabel}>State: *</p>
                        <AfitdCountryAutocomplete {...defns.state} />
                        {/* <span {...defns.stateErrorText}>State/Province is required</span> */}
                    </Grid>
                    <Grid {...defns.itemGrid}>
                        <p {...defns.fieldLabel}>City: *</p>
                        <AfitdCountryAutocomplete {...defns.city} />
                        {/* <span {...defns.cityErrorText}>City Name is required</span> */}
                    </Grid>
                    <Grid {...defns.itemGrid}>
                        <p {...defns.fieldLabel}>Telephone Number: *</p>
                        <Input
                            country={countryCode}
                            international
                            withCountryCallingCode
                            className={phoneError ? classes.phoneInputError : classes.phoneInput}
                            value={customerData?.phone}
                            onChange={val => onChangePhone(val)}
                            placeholder="Ex. +1 (301) 567-9544"
                        />
                        {phoneError && (
                            <AfitdTypography variant="body2" className={classes.phoneInputErrorMessage}>
                                {phoneError && customerData?.phone.length > 2 ? 'Phone number is not valid' : 'Telephone is required'}
                            </AfitdTypography>
                        )}
                    </Grid>
                    <Grid {...defns.itemGrid}>
                        <p {...defns.fieldLabel}>Point of Contact Name: *</p>
                        <AfitdTextField {...defns.contactName} />
                    </Grid>
                </Grid>
                <Grid {...defns.gridBodyFooter}>
                    {smMatches ? null : <Grid {...defns.footerGridItem}></Grid>}

                    <Grid {...defns.progressGrid}>
                        <p>
                            <b>1 of 5</b>
                        </p>
                        <BorderLinearProgress {...defns.progress} />
                    </Grid>
                    <Grid {...defns.footerGridItem}>
                        <div {...defns.nextButtonContainer}>
                            <AfitdButton {...defns.nextButton}>
                                {isLoading ? (
                                    <CircularProgress size={25} />
                                ) : (
                                    <>
                                        Next {` `}
                                        &nbsp;
                                        <ArrowRightAltIcon fontSize="small" />{' '}
                                    </>
                                )}
                            </AfitdButton>
                        </div>
                    </Grid>
                </Grid>
            </Container>
        </div>
    );
};

export default Step1Page;
